/* eslint-disable jsdoc/check-alignment */
import { Button } from '@studio/design-system/button';
import type { Api as DialogApi, Props } from '@studio/design-system/dialog';
import { Ellipses } from '@studio/design-system/label';
import { Message, Prompt } from '@studio/design-system/prompt';
import type { ReactElement } from 'react';
import { forwardRef } from 'react';

import MultiBridgeRequestIconSvg from './multi-bridge-linking-request.svg';

export type MultiBridgeRequestDialogProps = Props & {
  /** Called when the dialog is confirmed. */
  onConfirm: () => void;

  /** Called when the dialog is dismissed. */
  onDismiss: () => void;

  /** The name of the Studio. */
  studioName: string;

  /** The email of the user requesting the link. */
  userEmail: string;

  unlinkPreapproval?: boolean;
};

/** Multi-Bridge Request Dialog */
export const MultiBridgeRequestDialog = forwardRef<DialogApi, MultiBridgeRequestDialogProps>(
  ({ onConfirm, onDismiss, studioName, userEmail }, forwardedRef): ReactElement => {
    const content = (
      <div className='bg-base-200 pt-12 text-black' data-theme='light'>
        <div className='flex flex-col items-center p-32'>
          <div>
            <img src={MultiBridgeRequestIconSvg} className='mx-auto mb-12' />
            <Ellipses title={studioName} className='mb-4 text-center text-lg font-bold'>
              {studioName}
            </Ellipses>
            <p className='text-center text-lg font-demibold text-primary'>{userEmail}</p>
          </div>
        </div>
        {/* Divider */}
        <hr className='my-4' />
        <br />
        <div className='mb-16 text-center text-lg'>
          <h2 className='font-semibold mb-20 text-xl'>Bridge Link Requested</h2>
          <div className='mb-32 flex items-center justify-center'>
            <p className='w-5/6 text-center text-[14px] font-medium leading-[150%] text-[#22293E]'>
              <span className='font-demibold'>{userEmail}</span> is requesting to link this Bridge
              to their Studio. Approving will disconnect the current Bridge and connect it to the
              new Studio.
            </p>
          </div>
        </div>
        {/* Buttons! */}
        <div className='flex items-center justify-center gap-12'>
          <Button variant='danger' onClick={onDismiss} className='h-[56px] rounded-[8px] px-32'>
            Deny
          </Button>
          <Button
            variant='primary'
            onClick={onConfirm}
            className='h-[56px] rounded-[8px] px-32 text-white'
          >
            Approve
          </Button>
        </div>
      </div>
    );

    return (
      <div>
        <Prompt
          className='h-[550px] w-[450px] rounded-12 border-t-12 border-primary p-0'
          message={<Message>{content}</Message>}
          ref={forwardedRef}
          dismissClassName='hidden'
        />
      </div>
    );
  }
);
